import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsThreeVerticalIcon, CalendarBlankIcon, EyeIcon } from '@phosphor-icons/react';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import {
  statusBadgeConfig,
  type DocumentRowActions,
  type DocumentRowPermissions,
} from './documentTableTypes';
import { DocumentActionButton } from './DocumentActionButton';
import { DocumentRowMenu } from './DocumentRowMenu';

interface DocumentTableRowProps {
  document: DocumentNodeWithDetailsDto;
  isSelected: boolean;
  isMenuOpen: boolean;
  permissions: DocumentRowPermissions;
  actions: DocumentRowActions;
  loadingDocumentId?: string | null;
  isHighlighted?: boolean;
  onSelect: (id: string) => void;
  onMenuToggle: (id: string | null) => void;
  menuRef: React.RefObject<HTMLDivElement>;
}

const formatDate = (date: Date) => {
  return new Date(date).toLocaleDateString('fr-FR');
};

const formatOptionalDate = (date: Date | null | undefined) =>
  date ? new Date(date).toLocaleDateString('fr-FR') : '—';

const stackedCellClassName =
  'flex w-full items-center justify-between gap-3 px-4 lg:table-cell lg:w-auto lg:px-4 lg:py-3';

const mobileLabelClassName = 'text-xs font-medium uppercase text-gray-500 lg:hidden';

export const DocumentTableRow = ({
  document,
  isSelected,
  isMenuOpen,
  permissions,
  actions,
  loadingDocumentId,
  isHighlighted = false,
  onSelect,
  onMenuToggle,
  menuRef,
}: DocumentTableRowProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!isHighlighted) {
      return;
    }
    rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isHighlighted]);

  const statusConfig = document.status ? statusBadgeConfig[document.status] : null;
  const hasDiffusion = (document.diffusionRecipientCount ?? 0) > 0;

  return (
    <tr
      ref={rowRef}
      className={`flex flex-wrap items-center gap-y-3 border-b border-gray-100 py-4 transition-colors lg:table-row lg:py-0 ${
        isHighlighted ? 'bg-blue-50 ring-2 ring-inset ring-primary' : 'hover:bg-gray-50'
      }`}
    >
      <td className="order-1 px-4 lg:table-cell lg:py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(document.id)}
          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
      </td>

      <td className="order-2 flex-1 lg:table-cell lg:px-4 lg:py-3">
        <span className="text-sm font-medium text-gray-900 lg:font-normal">{document.name}</span>
      </td>

      <td className={`${stackedCellClassName} order-4 lg:order-none`}>
        <span className={mobileLabelClassName}>{t('documents.table.columns.status')}</span>
        {statusConfig && (
          <span
            className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.className}`}
          >
            {t(statusConfig.label)}
          </span>
        )}
      </td>

      <td className={`${stackedCellClassName} order-5 lg:order-none`}>
        <span className={mobileLabelClassName}>{t('documents.table.columns.opens')}</span>
        {hasDiffusion && actions.onShowReaders ? (
          <button
            type="button"
            onClick={() => actions.onShowReaders!(document)}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-sm font-medium text-primary transition hover:bg-primary hover:bg-opacity-10"
          >
            <EyeIcon size={16} />
            {`${document.diffusionOpenedCount ?? 0}/${document.diffusionRecipientCount ?? 0}`}
          </button>
        ) : (
          <span className="text-sm text-gray-600">—</span>
        )}
      </td>

      <td className={`${stackedCellClassName} order-6 lg:order-none`}>
        <span className={mobileLabelClassName}>{t('documents.table.columns.update_date')}</span>
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <CalendarBlankIcon size={16} className="text-gray-400" />
          {formatDate(document.updatedAt)}
        </div>
      </td>

      <td className={`${stackedCellClassName} order-7 lg:order-none`}>
        <span className={mobileLabelClassName}>{t('documents.table.columns.diffusion_date')}</span>
        <span className="text-sm text-gray-600">{formatOptionalDate(document.lastDiffusedAt)}</span>
      </td>

      <td className="order-8 w-full px-4 lg:order-none lg:table-cell lg:w-auto lg:py-3">
        <DocumentActionButton
          document={document}
          permissions={permissions}
          actions={actions}
          isLoading={loadingDocumentId === document.documentId}
        />
      </td>

      <td className="order-3 relative px-4 lg:order-none lg:table-cell lg:py-3">
        <button
          ref={buttonRef}
          onClick={() => onMenuToggle(isMenuOpen ? null : document.id)}
          className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)] transition"
        >
          <DotsThreeVerticalIcon size={20} className="text-gray-900" />
        </button>

        {isMenuOpen && (
          <DocumentRowMenu
            ref={menuRef}
            document={document}
            permissions={permissions}
            actions={actions}
            onClose={() => onMenuToggle(null)}
          />
        )}
      </td>
    </tr>
  );
};
